import type {TimelineRoleDefinition} from '@packages/systems/ix3/core';
import type {FeatureFlagName} from '@packages/systems/feature-config/shared';
import {
  IX3_WF_EXTENSION_KEYS,
  MODAL_TIMELINE_ROLES,
  POPOVER_TIMELINE_ROLES,
  type IX3ComponentId,
} from '@packages/systems/ix3-wf-plugins/shared';

/**
 * Components IX3 animates, each described once.
 *
 * Every per-component key list and check (the schema's invariants, the store,
 * the code-views lane, the Designer Extensions host, page automation, the
 * Designer's trigger and target registrations) is derived from these entries,
 * so a new component adds an entry here and one adapter per environment rather
 * than a line in each list. Navbar and Dropdown still have
 * hand-written entries in those lists; each derived list is "those plus the
 * manifest", and a drift test fails if a key is in both.
 *
 * Data only, and its own package so the published runtime, which never imports
 * it, carries none of it. The runtime keeps its own DOM contract per component,
 * pinned to these entries by a drift test.
 *
 * Saved data does not depend on this file: trigger tuples, target keys and
 * `pluginConfig` shapes are the same whether a component is described here or
 * by hand.
 *
 * The set is closed on purpose: only entries here can be authored or pass
 * validation, and the derived lists are built once at import. That keeps a
 * missing adapter a compile error. The runtime's trigger contracts are open,
 * but a component registered only there (a code component, DevLink) is still
 * rejected by validation and the Designer. Opening the set means replacing
 * the derived constants with accessors over a registry.
 */

/** A part of a component that an action or trigger can target. */
export type ComponentPart = {
  key: IX3_WF_EXTENSION_KEYS;
  /**
   * `element` is the component's own element. `backdrop` is its `::backdrop`:
   * it stores no path of its own and only exists while the component is open.
   */
  kind: 'element' | 'backdrop';
  /**
   * For a part that triggers can fire from: which triggers can, and the role
   * they must route to. `click-only` is a part where only the Click trigger
   * can tell a hit on it from one on the component (a modal's `::backdrop`
   * lands on the dialog itself). `firing` is a part that is its own element
   * (a popover's click mask), so any trigger whose event it raises can fire.
   */
  triggers?: {allowed: 'click-only' | 'firing'; role: string};
  /**
   * `false` for a part that stores no path of its own (a popover's backdrop is
   * resolved from the interaction's popovers). A stored path is still accepted
   * for data written before the part went path-free.
   */
  storesPath?: false;
};

export type ComponentManifestEntry = {
  /** Keys every per-environment adapter record. */
  id: IX3ComponentId;
  /** For UI copy and error messages. */
  noun: {singular: string; plural: string};
  /**
   * The flag that gates the component's trigger everywhere. Only its type is
   * imported, so the manifest carries no flag table.
   */
  flag: FeatureFlagName;
  /**
   * Whether the Designer, the code-views lane, Designer Extensions and page
   * automation may create this component's interactions. Off while the
   * authoring side is still being built, so a live flag never exposes a
   * half-built component; validation and the runtime include it either way.
   */
  authoring: boolean;
  /** `instance`: each interaction names one or more component instances. */
  binding: 'instance';
  trigger: {
    key: IX3_WF_EXTENSION_KEYS;
    roles: Readonly<Record<string, TimelineRoleDefinition>>;
    /** The interaction needs a timeline for every role. */
    requiresFullRoleSet: boolean;
    /**
     * The runtime plays the trigger's role timelines on one shared GSAP
     * timeline, unconditionally. Separate from `requiresFullRoleSet`, which is
     * only a validation rule.
     */
    ownsRoleSet: boolean;
    /** Every activation is a role callback: no playback controls. */
    emitsRolesAlways: boolean;
  };
  /**
   * `held-transition`: the component's site module holds each state change
   * until the role's timeline reports back.
   */
  protocol: 'held-transition';
  parts: readonly ComponentPart[];
};

export const POPOVER_COMPONENT = {
  id: 'popover',
  noun: {singular: 'popover', plural: 'popovers'},
  flag: 'ff-dsys-7107-popovers',
  authoring: true,
  binding: 'instance',
  trigger: {
    key: IX3_WF_EXTENSION_KEYS.POPOVER,
    roles: POPOVER_TIMELINE_ROLES,
    requiresFullRoleSet: true,
    ownsRoleSet: true,
    emitsRolesAlways: true,
  },
  protocol: 'held-transition',
  parts: [
    {key: IX3_WF_EXTENSION_KEYS.POPOVER_ELEMENT, kind: 'element'},
    {
      key: IX3_WF_EXTENSION_KEYS.POPOVER_BACKDROP,
      kind: 'backdrop',
      triggers: {allowed: 'firing', role: POPOVER_TIMELINE_ROLES.CLOSE.role},
      storesPath: false,
    },
  ],
} as const satisfies ComponentManifestEntry;

export const MODAL_COMPONENT = {
  id: 'modal',
  noun: {singular: 'modal', plural: 'modals'},
  flag: 'ff-dsys-7107-popovers',
  authoring: true,
  binding: 'instance',
  trigger: {
    key: IX3_WF_EXTENSION_KEYS.MODAL,
    roles: MODAL_TIMELINE_ROLES,
    requiresFullRoleSet: true,
    ownsRoleSet: true,
    emitsRolesAlways: true,
  },
  protocol: 'held-transition',
  parts: [
    {key: IX3_WF_EXTENSION_KEYS.MODAL_ELEMENT, kind: 'element'},
    {
      key: IX3_WF_EXTENSION_KEYS.MODAL_BACKDROP,
      kind: 'backdrop',
      triggers: {allowed: 'click-only', role: MODAL_TIMELINE_ROLES.CLOSE.role},
    },
  ],
} as const satisfies ComponentManifestEntry;

const ENTRIES = [POPOVER_COMPONENT, MODAL_COMPONENT] as const;

export type ComponentId = IX3ComponentId;

export const COMPONENT_MANIFEST: readonly ComponentManifestEntry[] = ENTRIES;

/** The roles a component's trigger owns, in order. */
export const componentRoles = (entry: ComponentManifestEntry): string[] =>
  Object.values(entry.trigger.roles).map(({role}) => role);

/** Every part key a component's trigger owns. */
export const componentPartKeys = (
  entry: ComponentManifestEntry
): IX3_WF_EXTENSION_KEYS[] => entry.parts.map(({key}) => key);

/** The manifest entries the authoring side may create. */
export const authoringComponents = (): ComponentManifestEntry[] =>
  COMPONENT_MANIFEST.filter(({authoring}) => authoring);
